import { type IntlShape } from 'react-intl';

import {
	DAYS_FOR_MONTH,
	DAYS_FOR_WEEK,
	DAYS_FOR_YESTERDAY,
	FURTHER_BACK_INDEX,
	MILLISECONDS_IN_DAY,
	MONTH_GROUP_INDEX,
	TODAY_GROUP_INDEX,
	WEEK_GROUP_INDEX,
	YESTERDAY_GROUP_INDEX,
} from '../../constants';
import type { Conversation } from '../../types';
import messages from './messages';

export type ConversationGroup = {
	label: string;
	conversations: Conversation[];
};

export const groupConversationsByTime = (
	conversations: Conversation[],
	formatMessage: IntlShape['formatMessage'],
): ConversationGroup[] => {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const tomorrow = new Date(today.getTime() + 1 * MILLISECONDS_IN_DAY);
	const yesterday = new Date(today.getTime() - DAYS_FOR_YESTERDAY * MILLISECONDS_IN_DAY);
	const sevenDaysAgo = new Date(today.getTime() - DAYS_FOR_WEEK * MILLISECONDS_IN_DAY);
	const thirtyDaysAgo = new Date(today.getTime() - DAYS_FOR_MONTH * MILLISECONDS_IN_DAY);

	const groups: ConversationGroup[] = [
		{ label: formatMessage(messages.today), conversations: [] },
		{ label: formatMessage(messages.yesterday), conversations: [] },
		{ label: formatMessage(messages.thisWeek), conversations: [] },
		{ label: formatMessage(messages.thisMonth), conversations: [] },
		{ label: formatMessage(messages.furtherBack), conversations: [] },
	];

	const sortedConversations = conversations.sort((a, b) => {
		if (!a.lastMessageTimestamp && !b.lastMessageTimestamp) {
			return 0;
		}
		if (!a.lastMessageTimestamp) {
			return 1;
		}
		if (!b.lastMessageTimestamp) {
			return -1;
		}

		// Sort by timestamp descending
		const dateA = new Date(a.lastMessageTimestamp);
		const dateB = new Date(b.lastMessageTimestamp);
		return dateB.getTime() - dateA.getTime();
	});

	sortedConversations.forEach((conversation) => {
		if (conversation.lastMessageTimestamp) {
			const messageDate = new Date(conversation.lastMessageTimestamp);

			// Exclude future dates (which shouldn't happen)
			if (messageDate < tomorrow) {
				if (messageDate >= today) {
					groups[TODAY_GROUP_INDEX].conversations.push(conversation); // Today
				} else if (messageDate >= yesterday) {
					groups[YESTERDAY_GROUP_INDEX].conversations.push(conversation); // Yesterday
				} else if (messageDate >= sevenDaysAgo) {
					groups[WEEK_GROUP_INDEX].conversations.push(conversation); // This past week
				} else if (messageDate >= thirtyDaysAgo) {
					groups[MONTH_GROUP_INDEX].conversations.push(conversation); // This past month
				} else {
					groups[FURTHER_BACK_INDEX].conversations.push(conversation); // Further back than this month
				}
				// Conversations older than 30 days are excluded (no else clause)
			}
		}
	});

	// Return only groups that have conversations
	return groups.filter((group) => group.conversations.length > 0);
};
